import authApi from '@api/authApi';
import BottomNavigation from '@components/bottomNavigation';
import ChangelogCheck from '@components/changelogCheck';
import Error from '@components/error';
import ErrorBoundary from '@components/errorBoundary';
import Header from '@components/header';
import Hotkeys from '@components/hotkeys';
import { LoadingIndicator } from '@components/loadingIndicator';
import LocationSpotlight from '@components/locationSpotlight';
import LoginPage from '@components/login/loginPage';
import ProviderContainer from '@components/providerContainer';
import Title from '@components/title';
import TitleUpdater from '@components/titleUpdater';
import useAuth from '@hooks/useAuth';
import useIsMobile from '@hooks/useIsMobile';
import useIsPublicRoute from '@hooks/useIsPublicRoute';
import useRestoreLocation from '@hooks/useRestoreLocation';
import { TranslationContextProvider } from '@i18n';
import { AppShell, MantineProvider } from '@mantine/core';
import { useHeadroom } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { Outlet, createRootRoute, retainSearchParams, useSearch } from '@tanstack/react-router';
import theme from '@theme';
import { useHandleError } from '@util/errors';
import tableTheme from '@util/tableTheme';
import { LoadingBoundary } from 'cross-state/react';
import { DatePickerProvider, TableSettingsProvider } from 'schummar-table';
import ExcelExporter from 'schummar-table/excelExporter';

export const Route = createRootRoute({
  component: RootPage,
  validateSearch(search): { locationId?: string } {
    return typeof search.locationId === 'string' ? { locationId: search.locationId } : {};
  },
  search: {
    middlewares: [retainSearchParams(true)],
  },
  errorComponent: ({ error }) => <Error error={error} />,
});

function RootPage() {
  const { user } = useAuth();
  const language = user?.language ?? navigator.language;
  const { locationId } = useSearch({ strict: false });

  return (
    <ProviderContainer
      key={locationId}
      providers={[
        <MantineProvider
          theme={theme}
          defaultColorScheme="auto"
          forceColorScheme={config.FEATURE_LIGHT_MODE ? undefined : 'dark'}
        />,
        <TranslationContextProvider locale={language} />,
        <TableSettingsProvider
          theme={tableTheme}
          additionalExporters={[{ action: 'download', exporter: new ExcelExporter() }]}
        />,
        <DatePickerProvider locale={language} />,
        <ModalsProvider />,
      ]}
    >
      <Notifications />
      <Content />
      <LocationSpotlight />
      <TitleUpdater />
      <Hotkeys />
    </ProviderContainer>
  );
}

function Content() {
  const isMobile = useIsMobile();
  const isPinned = useHeadroom({ fixedAt: isMobile ? 100 : Infinity });
  const isPublicRoute = useIsPublicRoute();

  const [auth, error] = authApi.auth.useCache();
  useHandleError(error);

  useRestoreLocation();

  if (isPublicRoute) {
    return <Outlet />;
  }

  if (auth === undefined) {
    return <LoadingIndicator delay={{ milliseconds: 100 }} size="grow" />;
  }

  if (!auth) {
    return <LoginPage />;
  }

  return (
    <Title>
      <AppShell
        header={{
          height: 80,
          offset: false,
          collapsed: !isPinned,
        }}
        footer={{
          height: 70,
        }}
      >
        <AppShell.Header
          css={{
            transitionProperty: 'transform',
          }}
        >
          <Header />
        </AppShell.Header>

        <AppShell.Main
          css={{
            display: 'flex',
            flexDirection: 'column',
            paddingTop: 'var(--app-shell-header-height)',
          }}
        >
          <LoadingBoundary fallback={<LoadingIndicator delay={{ milliseconds: 100 }} size="grow" />}>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </LoadingBoundary>
        </AppShell.Main>

        <AppShell.Footer
          css={{
            transitionProperty: 'transform',
          }}
        >
          <BottomNavigation />
        </AppShell.Footer>
      </AppShell>

      <ChangelogCheck />
    </Title>
  );
}
